import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TwoFactorCard from "@/components/TwoFactorCard";
import PasswordCard from "@/components/PasswordCard";
import PushDiagnosticsCard from "@/components/PushDiagnosticsCard";
import {
  DeviceMobile, Desktop, ShieldCheck, SignOut, Warning, Clock,
} from "@phosphor-icons/react";

const isMobile = (device) =>
  /iphone|ipad|android/i.test(device || "");

export default function AccountSettings() {
  const { user, logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});
  const [toast, setToast] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (sid, isCurrent) => {
    const msg = isCurrent
      ? "Revoke this session? You'll be signed out immediately."
      : "Revoke this session? The device will need to sign in again.";
    if (!window.confirm(msg)) return;
    setBusy((b) => ({ ...b, [sid]: true }));
    try {
      await api.delete(`/auth/sessions/${sid}`);
      if (isCurrent) {
        // Server already cleared cookies — do a full logout on the client
        await logout();
        window.location.href = "/login";
        return;
      }
      setToast("Session revoked.");
      setTimeout(() => setToast(""), 2500);
      await load();
    } catch (e) {
      setToast(`Revoke failed: ${formatApiError(e.response?.data?.detail) || e.message}`);
      setTimeout(() => setToast(""), 4000);
    } finally {
      setBusy((b) => ({ ...b, [sid]: false }));
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Sign out every other device? Only this browser will stay signed in.")) return;
    setBusy((b) => ({ ...b, __all: true }));
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      setToast(`Revoked ${data.revoked} other session${data.revoked === 1 ? "" : "s"}.`);
      setTimeout(() => setToast(""), 3500);
      await load();
    } catch (e) {
      setToast(`Failed: ${formatApiError(e.response?.data?.detail) || e.message}`);
      setTimeout(() => setToast(""), 4000);
    } finally {
      setBusy((b) => ({ ...b, __all: false }));
    }
  };

  const active = sessions.filter((s) => s.state === "active");
  const otherActive = active.filter((s) => !s.is_current);

  return (
    <Layout>
      <PageHeader
        title="Account & sessions"
        subtitle="Review active devices, sign out remotely, and rotate access."
        actions={
          otherActive.length > 0 && (
            <button onClick={revokeOthers} disabled={busy.__all}
              data-testid="revoke-others-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-md transition-colors">
              <SignOut size={14} weight="bold" />
              {busy.__all ? "Revoking…" : `Sign out other devices (${otherActive.length})`}
            </button>
          )
        }
      />
      <div className="p-8">
        {toast && (
          <div data-testid="account-toast"
            className="mb-4 text-xs px-3 py-2 border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan rounded">
            {toast}
          </div>
        )}

        {/* Profile card */}
        <section className="sw-card p-6 mb-6" data-testid="account-profile">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-brand-cyan/15 text-brand-cyan text-base font-bold flex items-center justify-center shrink-0">
              {(user?.name || user?.email || "?").split(/[\s@.]+/).slice(0, 2).map(s => (s || " ")[0]?.toUpperCase()).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold" data-testid="account-name">
                {user?.name || "—"}
              </div>
              <div className="text-sm text-slate-400" data-testid="account-email">
                {user?.email}
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-0.5 border border-brand-cyan/30 bg-brand-cyan/5 text-brand-cyan rounded">
                <ShieldCheck size={11} weight="bold" /> {user?.role || "user"}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">
            SentryWatch defaults to passwordless magic-link sign-in. Admin accounts can
            optionally set a password (below) for command-line-friendly access. 2FA still applies.
            Every sign-in creates a new session below — revoke any session anytime.
          </p>
        </section>

        {/* Two-factor authentication */}
        <div className="mb-6">
          <TwoFactorCard />
        </div>

        {/* Password sign-in (admin only) */}
        <div className="mb-6">
          <PasswordCard />
        </div>

        {/* Push diagnostics */}
        <div className="mb-6">
          <PushDiagnosticsCard />
        </div>

        {/* Sessions list */}
        <section className="sw-card overflow-hidden" data-testid="sessions-card">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-white tracking-tight">Active sessions</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Sessions expire automatically after 90 days of inactivity.
              </p>
            </div>
            {error && <div className="text-xs text-red-400" data-testid="sessions-error">{error}</div>}
          </div>

          {loading ? (
            <div className="p-12 text-center text-sm text-slate-500">Loading sessions…</div>
          ) : sessions.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">No sessions found.</div>
          ) : (
            <table className="w-full text-sm" data-testid="sessions-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-6">Device</th>
                  <th className="text-left py-3 px-6">Last seen</th>
                  <th className="text-left py-3 px-6">IP</th>
                  <th className="text-left py-3 px-6">Signed in</th>
                  <th className="text-right py-3 px-6 w-40"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const Icon = isMobile(s.device) ? DeviceMobile : Desktop;
                  const stateTone = s.state === "active"
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : s.state === "revoked"
                    ? "border-red-500/40 bg-red-500/10 text-red-300"
                    : "border-slate-500/40 bg-slate-500/10 text-slate-300";
                  return (
                    <tr key={s.id} data-testid={`session-row-${s.id}`}
                      className="border-t border-border hover:bg-white/5">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <Icon size={20} weight="duotone" className="text-brand-cyan shrink-0" />
                          <div>
                            <div className="text-white text-sm font-medium">
                              {s.device} · {s.browser}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {s.is_current && (
                                <span data-testid={`session-current-${s.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan rounded">
                                  This device
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded ${stateTone}`}>
                                {s.state}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-slate-300">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Clock size={12} weight="regular" className="text-slate-500" />
                          {formatRelative(s.last_seen_at)}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-slate-400 text-xs">
                        {s.ip || "—"}
                      </td>
                      <td className="py-4 px-6 text-slate-400 text-xs">
                        {formatRelative(s.created_at)}
                      </td>
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        {s.state === "active" ? (
                          <button onClick={() => revoke(s.id, s.is_current)}
                            disabled={busy[s.id]}
                            data-testid={`revoke-session-${s.id}`}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border transition-colors ${
                              s.is_current
                                ? "border-red-500/40 text-red-300 hover:bg-red-500/10"
                                : "border-border text-slate-300 hover:border-red-500/40 hover:text-red-300 hover:bg-red-500/10"
                            }`}>
                            <SignOut size={12} weight="bold" />
                            {busy[s.id] ? "…" : s.is_current ? "Sign out" : "Revoke"}
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        {sessions.some((s) => s.state === "revoked") && (
          <p className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
            <Warning size={11} /> Revoked sessions stay visible for 30 days for audit.
          </p>
        )}
      </div>
    </Layout>
  );
}

function formatRelative(iso) {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
